import { useEffect, useState } from 'react'
import { FaTrash, FaCog } from 'react-icons/fa'
import './ProjectCard.css'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useGetProjectAccessRules } from '../../stores/projectsStore'
import ProjectSettings from './ProjectSettings'
import { getStartingAngleDisplayName } from '../../utils/projectUtils'
import { useUser } from '@/context/UserContext'
import { getAxiosInstance } from '@/network/axiosInstance'
// Removed unused import

export const ProjectCard = ({
  project,
  userId,
  onNavigate,
  onDelete,
  onOpen,
  onSettingsChanged,
}) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [accessRules, setAccessRules] = useState({ accessRules: [] as any })
  const [showSettings, setShowSettings] = useState(false)
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null)
  const [actionLogsSummary, setActionLogsSummary] = useState<{
    total: number
    successful: number
    failed: number
    inProgress: number
  } | null>(null)
  const [activityData, setActivityData] = useState<Record<string, number>>({})

  const user = useUser()
  const getProjectAccessRules = useGetProjectAccessRules()

  const fetchAccessRules = async () => {
    getProjectAccessRules.execute({
      id: project.id,
      onSuccess: (response) => {
        console.log('Access rules:', response.data)
        if (response.data.accessRules != null) {
          setAccessRules(response.data)
        }
      },
      onError: (error) => {
        console.error('Error fetching access rules:', error)
      },
    })
  }

  const fetchActionLogsData = async () => {
    if (!user?.id || !project.id) return

    try {
      const axiosInstance = getAxiosInstance()
      const response = await axiosInstance.get('/api/subscriptions/action-logs', {
        params: {
          projectId: project.id,
          range: '90d'
        }
      })

      const logs = response.data.logs || []

      // Calculate summary stats
      const summary = {
        total: logs.length,
        successful: logs.filter((log: any) => log.success).length,
        failed: logs.filter((log: any) => log.success === false).length,
        inProgress: logs.filter((log: any) => !log.completedAt).length
      }
      setActionLogsSummary(summary)

      // Calculate activity data for last 91 days
      const activity: Record<string, number> = {}
      logs.forEach((log: any) => {
        const date = new Date(log.startedAt).toISOString().split('T')[0]
        activity[date] = (activity[date] || 0) + 1
      })
      setActivityData(activity)
    } catch (error) {
      console.error('Error fetching action logs:', error)
    }
  }

  useEffect(() => {
    //const hasFullAccess = project.settings[0]?.accessRules?.find(
    //  (rule: AccessRule) => rule.userId == userId && rule.accessLevel == 'full'
    //)
    const hasFullAccess = true

    if (hasFullAccess) {
      setShowSettings(true)
    }

    // Fetch action logs data for summary and activity calendar
    fetchActionLogsData()
  }, [project.id, user?.id])

  // Fetch settings and open modal
  const toggleModal = async () => {
    if (!isModalOpen) {
      await fetchAccessRules()
    }
    setModalOpen(!isModalOpen)
  }

  const handleKeyPress = (e, action, allowSpace = false) => {
    // Only trigger on Enter, optionally on Space if allowed
    if (e.key === 'Enter' || (allowSpace && e.key === ' ')) {
      action()
    }
  }

  // Adjusted the handleCardClick to be a curried function that takes the projectId
  const handleCardClick = (projectId) => (e) => {
    // Prevent navigation if the event target is an input or textarea (e.g., editing in modal)
    const tag = (e.target && (e.target as HTMLElement).tagName?.toLowerCase()) || '';
    if (tag === 'input' || tag === 'textarea') return;
    onOpen(projectId, project.projectName)
    if (!(e.target as HTMLElement).closest('.project-card-menu')) {
      onNavigate(projectId)
    }
  }

  const onAccessRulesUpdate = async () => {
    await fetchAccessRules()
  }

  const onSystemAgentEnabledChange = () => {
    // Reload project data when system agent setting changes
    if (onSettingsChanged) {
      onSettingsChanged(project.id)
    }
  }

  // Helper to get the correct settings object
  const getSettingsObject = () => {
    if (Array.isArray(project.settings) && project.settings.length > 0) {
      return project.settings[0]
    } else if (project.settings && typeof project.settings === 'object') {
      return project.settings
    }
    return null
  }

  // Generate activity calendar data for last 90 days
  const generateActivityCalendar = () => {
    const today = new Date()
    const startDate = new Date(today)
    startDate.setDate(today.getDate() - 90) // 91 days including today (13 weeks * 7 = 91)

    const calendar: { date: string, count: number, intensity: number }[] = []
    const currentDate = new Date(startDate)

    while (currentDate <= today) {
      const dateStr = currentDate.toISOString().split('T')[0]
      const activityCount = activityData[dateStr] || 0

      calendar.push({
        date: dateStr,
        count: activityCount,
        intensity: getActivityIntensity(activityCount)
      })

      currentDate.setDate(currentDate.getDate() + 1)
    }

    return calendar
  }

  // Get activity intensity for color coding
  const getActivityIntensity = (count: number) => {
    if (count === 0) return 0
    if (count <= 2) return 1
    if (count <= 5) return 2
    if (count <= 10) return 3
    return 4
  }

  // Render activity calendar
  const renderActivityCalendar = () => {
    const calendar = generateActivityCalendar()
    const weeks: { date: string, count: number, intensity: number }[][] = []

    // Group days into weeks
    for (let i = 0; i < calendar.length; i += 7) {
      weeks.push(calendar.slice(i, i + 7))
    }

    return (
      <div className="project-card-activity-calendar">
        <div className="activity-calendar-title">Activity (Last 91 days)</div>
        <div className="activity-calendar-content">
          <div className="activity-calendar-grid">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="activity-week">
                {week.map((day) => (
                  <div
                    key={day.date}
                    className={`activity-day intensity-${day.intensity}`}
                    title={`${day.date}: ${day.count} actions`}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="activity-legend">
            <span>More</span>
            <div className="legend-squares">
              {[4, 3, 2, 1, 0].map(intensity => (
                <div key={intensity} className={`legend-square intensity-${intensity}`} />
              ))}
            </div>
            <span>Less</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      key={project.id}
      onClick={handleCardClick(project.id)}
      onKeyDown={(e) => {
        // Prevent navigation if editing text fields
        const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        handleKeyPress(e, () => onNavigate(project.id), true)
      }}
      role="button"
      tabIndex={0}
      className={`project-card-container ${project.createdByUserId != userId ? 'bg-sky-400 dark:bg-sky-700' : ''
        }`}
    >
      <div className="project-card-text">
        <div className="project-card-main-content">
          <strong className="project-card-title">{project.projectName}</strong>
          <div className="project-card-description">
            {project.projectDescription}
          </div>
          <div className="project-card-metadata">
            <span className="project-type">
              {project.projectType === 'collaboration' ? '👥 Collaboration' : '👤 Personal'}
            </span>
            {project.organization && (
              <span className="organization-name">
                🏢 {project.organization.name}
              </span>
            )}
            {project.startingAngle && (
              <span className="starting-angle">
                📋 {getStartingAngleDisplayName(project.startingAngle)}
              </span>
            )}
            {project.passwordProtected && (
              <span className="password-protected">
                🔒 Protected
              </span>
            )}
            {project.isInitialized === false && (
              <span className="not-initialized">
                ⚠️ Not Initialized
              </span>
            )}
          </div>
          {project.systemAgentEnabled !== null && (
            <div className="project-card-system-agent">
              Artificial Intelligence is {project.systemAgentEnabled ? 'Enabled' : 'Disabled'}
            </div>
          )}
        </div>

        <div className="project-card-ai-section">
          {/* Rotated AI Activity Label */}
          {(actionLogsSummary && actionLogsSummary.total > 0) || Object.keys(activityData).length > 0 ? (
            <div className="ai-activity-label">
              AI Activity<br />Last 91 Days
            </div>
          ) : null}

          {/* AI Action Logs Summary */}
          {actionLogsSummary && actionLogsSummary.total > 0 && (
            <div className="project-card-ai-summary">
              <div className="ai-summary-title">AI Actions (90 days)</div>
              <div className="ai-summary-stats">
                <span className="stat-item">
                  <span className="stat-value">{actionLogsSummary.total}</span>
                  <span className="stat-label">Total</span>
                </span>
                <span className="stat-item">
                  <span className="stat-value success">{actionLogsSummary.successful}</span>
                  <span className="stat-label">Success</span>
                </span>
                <span className="stat-item">
                  <span className="stat-value failed">{actionLogsSummary.failed}</span>
                  <span className="stat-label">Failed</span>
                </span>
                <span className="stat-item">
                  <span className="stat-value in-progress">{actionLogsSummary.inProgress}</span>
                  <span className="stat-label">In Progress</span>
                </span>
              </div>
            </div>
          )}

          {/* Activity Calendar */}
          {Object.keys(activityData).length > 0 && renderActivityCalendar()}
        </div>
      </div>
      <div
        className="project-card-menu"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => handleKeyPress(e, () => onDelete(project.id))} // only Enter triggers delete
        role="menu"
        tabIndex={-1}
      >
        <span
          role="button"
          tabIndex={0}
          className="project-card-menu-button"
          onKeyDown={(e) => handleKeyPress(e, () => onDelete(project.id))} // only Enter triggers delete
          onClick={() => onDelete(project.id)}
          onMouseEnter={() => setHoveredIcon('trash')}
          onMouseLeave={() => setHoveredIcon(null)}
        >
          <FaTrash
            size={18}
            style={{
              color:
                hoveredIcon === 'trash'
                  ? '#cc2222'
                  : '#996666',
              transition: 'color 0.2s',
            }}
          />
        </span>
        {showSettings && (
          <span
            role="button"
            tabIndex={0}
            className="project-card-menu-button"
            onClick={() => toggleModal()}
            onMouseEnter={() => setHoveredIcon('cog')}
            onMouseLeave={() => setHoveredIcon(null)}
          >
            <FaCog
              size={18}
              style={{
                color:
                  hoveredIcon === 'cog'
                    ? '#22cc22'
                    : '#4444ff',
                transition: 'color 0.2s',
              }}
            />
          </span>
        )}
        <Dialog open={isModalOpen} onOpenChange={toggleModal}>
          <DialogContent className="sm:max-w-[700px]">
            <DialogHeader>
              <DialogTitle>Project Settings</DialogTitle>
            </DialogHeader>
            {getSettingsObject() ? (
              <ProjectSettings
                projectId={project.id}
                projectName={project.projectName}
                projectDescription={project.projectDescription}
                projectType={project.projectType}
                organizationName={project.organization?.name}
                settings={getSettingsObject()}
                accessRules={accessRules.accessRules}
                closeModal={() => setModalOpen(false)}
                onAccessRulesUpdate={onAccessRulesUpdate}
                systemAgentEnabled={project.systemAgentEnabled}
                onSystemAgentEnabledChange={onSystemAgentEnabledChange}
              />
            ) : (
              <div className="p-4 text-center text-gray-500">Loading project details...</div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      {/* Display node and edge counts and created and updatedat datetime */}
      <div className="project-card-counts">
        [Created: {project.createdAt ? new Date(project.createdAt).toLocaleString() : 'N/A'}] Updated: {project.updatedAt ? new Date(project.updatedAt).toLocaleString() : 'N/A'}][ {project.nodes?.length || 0} Nodes ] [ {project.edges?.length || 0} Edges ]
      </div>
    </div>
  )
}
